import React from 'react';
import { motion } from 'framer-motion';
import { FileText, CheckCircle, AlertCircle, Scale, UserX, Shield } from 'lucide-react';
export function TermsOfServicePage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-1047">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1048">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-1049">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-1050">
            <span className="w-8 h-px bg-teal" data-id="element-1051"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-1052">
              Legal
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-1053"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-1054">
            Terms of Service
          </h1>

          <p className="text-lg text-ink-muted" data-id="element-1055">
            Last Updated:{' '}
            {new Date().toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          })}
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1056">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1057">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-1058">
            These Terms of Service ("Terms") govern your access to and use of
            SyNeural's platform, services, and website (collectively, the
            "Services"). By accessing or using our Services, you agree to be
            bound by these Terms.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1059">
            If you do not agree to these Terms, you may not access or use our
            Services. If you are using our Services on behalf of an
            organization, you represent that you have the authority to bind that
            organization to these Terms.
          </p>
        </motion.div>
      </section>

      {/* Content Sections */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto space-y-16" data-id="element-1060">
        {/* Acceptance of Terms */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1061">
          <div className="flex items-center gap-4 mb-6" data-id="element-1062">
            <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-1063">
              <CheckCircle size={24} data-id="element-1064" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1065">
              Acceptance of Terms
            </h2>
          </div>

          <p className="text-ink-muted leading-relaxed" data-id="element-1066">
            By creating an account, accessing our platform, or using any of our
            Services, you acknowledge that you have read, understood, and agree
            to be bound by these Terms, as well as our Privacy Policy. These
            Terms constitute a legally binding agreement between you and
            SyNeural.
          </p>
        </motion.div>

        {/* Eligibility */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1067">
          <div className="flex items-center gap-4 mb-6" data-id="element-1068">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-1069">
              <UserX size={24} data-id="element-1070" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1071">
              Eligibility
            </h2>
          </div>

          <p className="text-ink-muted leading-relaxed mb-4" data-id="element-1072">
            To use our Services, you must:
          </p>
          <ul className="list-disc list-inside space-y-2 text-ink-muted ml-4" data-id="element-1073">
            <li data-id="element-1074">
              Be at least 13 years of age (or the age of majority in your
              jurisdiction)
            </li>
            <li data-id="element-1075">Have the legal capacity to enter into a binding agreement</li>
            <li data-id="element-1076">
              Not be prohibited from using our Services under applicable law
            </li>
            <li data-id="element-1077">Provide accurate and complete registration information</li>
          </ul>
          <p className="text-ink-muted leading-relaxed mt-4" data-id="element-1078">
            For users under 18, access through educational institutions or with
            parental consent may be required.
          </p>
        </motion.div>

        {/* Account Registration and Security */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1079">
          <div className="flex items-center gap-4 mb-6" data-id="element-1080">
            <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-1081">
              <Shield size={24} data-id="element-1082" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1083">
              Account Registration and Security
            </h2>
          </div>

          <div className="space-y-4 text-ink-muted leading-relaxed" data-id="element-1084">
            <p data-id="element-1085">When you create an account, you agree to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4" data-id="element-1086">
              <li data-id="element-1087">Provide accurate, current, and complete information</li>
              <li data-id="element-1088">Maintain and update your information to keep it accurate</li>
              <li data-id="element-1089">
                Maintain the security and confidentiality of your account
                credentials
              </li>
              <li data-id="element-1090">
                Notify us immediately of any unauthorized access or security
                breach
              </li>
              <li data-id="element-1091">
                Accept responsibility for all activities that occur under your
                account
              </li>
            </ul>
            <p data-id="element-1092">
              You may not share your account credentials, create multiple
              accounts, or allow others to use your account. SyNeural reserves
              the right to suspend or terminate accounts that violate these
              requirements.
            </p>
          </div>
        </motion.div>

        {/* Use of Services */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1093">
          <div className="flex items-center gap-4 mb-6" data-id="element-1094">
            <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-1095">
              <FileText size={24} data-id="element-1096" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1097">
              Permitted Use
            </h2>
          </div>

          <p className="text-ink-muted leading-relaxed mb-4" data-id="element-1098">
            You may use our Services for lawful purposes in accordance with
            these Terms. You agree not to:
          </p>
          <ul className="list-disc list-inside space-y-2 text-ink-muted ml-4" data-id="element-1099">
            <li data-id="element-1100">
              Violate any applicable laws, regulations, or third-party rights
            </li>
            <li data-id="element-1101">
              Use the Services for any fraudulent, harmful, or malicious purpose
            </li>
            <li data-id="element-1102">
              Attempt to gain unauthorized access to our systems or other users'
              accounts
            </li>
            <li data-id="element-1103">
              Interfere with or disrupt the integrity or performance of the
              Services
            </li>
            <li data-id="element-1104">
              Reverse engineer, decompile, or attempt to extract source code
            </li>
            <li data-id="element-1105">
              Use automated tools (bots, scrapers) without our written
              permission
            </li>
            <li data-id="element-1106">Misrepresent your identity or affiliation</li>
            <li data-id="element-1107">
              Share, sell, or redistribute assessment content or results without
              authorization
            </li>
            <li data-id="element-1108">Use the Services to harass, abuse, or harm others</li>
          </ul>
        </motion.div>

        {/* Intellectual Property */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1109">
          <div className="flex items-center gap-4 mb-6" data-id="element-1110">
            <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-1111">
              <Scale size={24} data-id="element-1112" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1113">
              Intellectual Property Rights
            </h2>
          </div>

          <div className="space-y-4 text-ink-muted leading-relaxed" data-id="element-1114">
            <p data-id="element-1115">
              All content, features, functionality, and materials available
              through our Services, including but not limited to text, graphics,
              logos, assessment frameworks, algorithms, software, and data
              compilations, are owned by SyNeural or its licensors and are
              protected by copyright, trademark, patent, and other intellectual
              property laws.
            </p>
            <p data-id="element-1116">
              We grant you a limited, non-exclusive, non-transferable, revocable
              license to access and use our Services for your personal or
              internal organizational use. This license does not include any
              right to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4" data-id="element-1117">
              <li data-id="element-1118">
                Reproduce, distribute, or create derivative works from our
                content
              </li>
              <li data-id="element-1119">
                Use our trademarks, logos, or branding without written
                permission
              </li>
              <li data-id="element-1120">
                Remove or alter any copyright, trademark, or proprietary notices
              </li>
              <li data-id="element-1121">Commercially exploit our Services or content</li>
            </ul>
          </div>
        </motion.div>

        {/* User Content and Data */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1122">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1123">
            User Content and Data
          </h2>

          <div className="space-y-4 text-ink-muted leading-relaxed" data-id="element-1124">
            <p data-id="element-1125">
              You retain ownership of any content, data, or information you
              provide through our Services ("User Content"). By providing User
              Content, you grant SyNeural a worldwide, non-exclusive,
              royalty-free license to use, process, analyze, and store such
              content to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4" data-id="element-1126">
              <li data-id="element-1127">Provide and improve our Services</li>
              <li data-id="element-1128">Generate assessment results and recommendations</li>
              <li data-id="element-1129">
                Conduct research and platform development (using anonymized
                data)
              </li>
              <li data-id="element-1130">Comply with legal obligations</li>
            </ul>
            <p data-id="element-1131">
              You represent and warrant that you have all necessary rights to
              provide User Content and that such content does not violate any
              third-party rights or applicable laws.
            </p>
          </div>
        </motion.div>

        {/* Payment and Subscriptions */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1132">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1133">
            Payment and Subscriptions
          </h2>

          <div className="space-y-4 text-ink-muted leading-relaxed" data-id="element-1134">
            <p data-id="element-1135">
              Certain features of our Services may require payment. If you
              purchase a subscription or paid service:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4" data-id="element-1136">
              <li data-id="element-1137">
                You agree to pay all applicable fees as described at the time of
                purchase
              </li>
              <li data-id="element-1138">
                Fees are non-refundable except as required by law or as
                specified in our refund policy
              </li>
              <li data-id="element-1139">
                Subscriptions automatically renew unless canceled before the
                renewal date
              </li>
              <li data-id="element-1140">
                We reserve the right to change pricing with advance notice
              </li>
              <li data-id="element-1141">
                You are responsible for all taxes associated with your purchase
              </li>
            </ul>
            <p data-id="element-1142">
              Failure to pay fees may result in suspension or termination of
              your access to paid Services.
            </p>
          </div>
        </motion.div>

        {/* Disclaimers and Limitations */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1143">
          <div className="flex items-center gap-4 mb-6" data-id="element-1144">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-1145">
              <AlertCircle size={24} data-id="element-1146" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1147">
              Disclaimers and Limitations of Liability
            </h2>
          </div>

          <div className="space-y-4 text-ink-muted leading-relaxed" data-id="element-1148">
            <p className="font-semibold" data-id="element-1149">
              THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT
              WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.
            </p>
            <p data-id="element-1150">
              SyNeural disclaims all warranties, including but not limited to
              warranties of merchantability, fitness for a particular purpose,
              non-infringement, and accuracy. We do not warrant that:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4" data-id="element-1151">
              <li data-id="element-1152">The Services will be uninterrupted, secure, or error-free</li>
              <li data-id="element-1153">
                Assessment results will be accurate, complete, or suitable for
                any particular purpose
              </li>
              <li data-id="element-1154">Any defects or errors will be corrected</li>
              <li data-id="element-1155">The Services will meet your specific requirements</li>
            </ul>
            <p className="font-semibold mt-6" data-id="element-1156">LIMITATION OF LIABILITY:</p>
            <p data-id="element-1157">
              To the maximum extent permitted by law, SyNeural shall not be
              liable for any indirect, incidental, special, consequential, or
              punitive damages, including but not limited to loss of profits,
              data, or opportunities, arising out of or related to your use of
              the Services, even if we have been advised of the possibility of
              such damages.
            </p>
            <p data-id="element-1158">
              Our total liability for any claims related to the Services shall
              not exceed the amount you paid us in the twelve months preceding
              the claim.
            </p>
          </div>
        </motion.div>

        {/* Indemnification */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1159">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1160">
            Indemnification
          </h2>

          <p className="text-ink-muted leading-relaxed" data-id="element-1161">
            You agree to indemnify, defend, and hold harmless SyNeural, its
            affiliates, officers, directors, employees, and agents from any
            claims, liabilities, damages, losses, costs, or expenses (including
            reasonable attorneys' fees) arising out of or related to: (a) your
            use of the Services; (b) your violation of these Terms; (c) your
            violation of any third-party rights; or (d) your User Content.
          </p>
        </motion.div>

        {/* Termination */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1162">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1163">
            Termination
          </h2>

          <div className="space-y-4 text-ink-muted leading-relaxed" data-id="element-1164">
            <p data-id="element-1165">
              You may terminate your account at any time by contacting us or
              using account settings. We may suspend or terminate your access to
              the Services at any time, with or without cause or notice,
              including if:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4" data-id="element-1166">
              <li data-id="element-1167">You violate these Terms</li>
              <li data-id="element-1168">Your use poses a security or legal risk</li>
              <li data-id="element-1169">You fail to pay applicable fees</li>
              <li data-id="element-1170">We discontinue the Services</li>
            </ul>
            <p data-id="element-1171">
              Upon termination, your right to use the Services will immediately
              cease. Provisions that by their nature should survive termination
              (including intellectual property rights, disclaimers, and
              limitations of liability) will remain in effect.
            </p>
          </div>
        </motion.div>

        {/* Governing Law and Dispute Resolution */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1172">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1173">
            Governing Law and Dispute Resolution
          </h2>

          <p className="text-ink-muted leading-relaxed" data-id="element-1174">
            These Terms shall be governed by and construed in accordance with
            the laws of [Jurisdiction], without regard to its conflict of law
            provisions. Any disputes arising out of or related to these Terms or
            the Services shall be resolved through binding arbitration in
            accordance with [Arbitration Rules], except that either party may
            seek injunctive relief in court. You waive any right to participate
            in class actions or class arbitrations.
          </p>
        </motion.div>

        {/* Changes to Terms */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1175">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1176">
            Changes to These Terms
          </h2>

          <p className="text-ink-muted leading-relaxed" data-id="element-1177">
            We reserve the right to modify these Terms at any time. We will
            notify you of material changes by posting the updated Terms on our
            website and updating the "Last Updated" date. Your continued use of
            the Services after such changes constitutes acceptance of the
            updated Terms. If you do not agree to the modified Terms, you must
            stop using the Services.
          </p>
        </motion.div>

        {/* Contact Information */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="bg-surface-muted p-8 rounded-lg border border-line" data-id="element-1178">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1179">
            Contact Us
          </h2>
          <p className="text-ink-muted leading-relaxed mb-4" data-id="element-1180">
            If you have questions about these Terms of Service, please contact
            us:
          </p>
          <div className="space-y-2 text-ink-muted" data-id="element-1181">
            <p data-id="element-1182">
              <strong data-id="element-1183">Email:</strong>{' '}
              <a href="mailto:legal@syneural.com" className="text-teal font-medium hover:underline" data-id="element-1184">
                legal@syneural.com
              </a>
            </p>
            <p data-id="element-1185">
              <strong data-id="element-1186">General Inquiries:</strong>{' '}
              <a href="mailto:contact@syneural.com" className="text-teal font-medium hover:underline" data-id="element-1187">
                contact@syneural.com
              </a>
            </p>
          </div>
        </motion.div>
      </section>
    </div>;
}